import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { CombiBonusState } from '@/domain/combi-bonus';
import { formatAmount } from '@/domain/player-balance';
import { Icon } from '@/ui/common/icon';

export interface CombiBonusBannerProps {
  bonus: CombiBonusState;
  currency?: string;
  className?: string;
}

function money(amount: number, currency: string): string {
  const formatted = formatAmount(amount);
  return currency ? `${currency}${formatted}` : formatted;
}

/** Legacy attractive-combi hint above the selection list. */
export function CombiBonusBanner({ bonus, currency = '', className }: CombiBonusBannerProps) {
  const { t } = useTranslation();

  const percent =
    bonus.kind === 'progress' ? bonus.nextPercent : bonus.percent;
  const fixedAmount =
    bonus.kind === 'progress' ? bonus.nextAmount : bonus.percent == null ? bonus.amount : null;

  return (
    <div
      role="status"
      className={cn('flex items-center gap-1.5 py-1 text-body-s text-status-warning', className)}
    >
      <Icon name="warning" size={16} className="shrink-0 text-status-warning" />
      {bonus.kind === 'progress' ? (
        <p>
          <span>
            {t('AddMoreForCombiBonus', {
              defaultValue: 'Add {{needed}} more for combi bonus',
              needed: bonus.selectionsNeeded,
            })}
          </span>
          {percent != null ? (
            <span className="ps-1 font-bold">{`+${percent}%`}</span>
          ) : fixedAmount != null ? (
            <span className="ps-1 font-bold">{money(fixedAmount, currency)}</span>
          ) : null}
        </p>
      ) : (
        <p>
          <span className="font-bold">
            {percent != null
              ? `${percent}%`
              : fixedAmount != null
                ? money(fixedAmount, currency)
                : money(bonus.bonusValue, currency)}
          </span>
          <span className="ps-1">{t('Bonus', { defaultValue: 'Bonus' })}</span>
        </p>
      )}
    </div>
  );
}
